import React, { useCallback, useEffect } from 'react';
import { getSearchParam } from 'helpers/getSearchParam';

import {
  CasinoFavoritesProvider,
  CasinoGamesProvider,
  CategoryGamesProvider,
  useCasinoNewState,
} from '@atlas/core';

import { CasinoFavoritesGames } from 'components/Pages/Casino/CasinoFavoritesGames';
import { useCasinoFilterState } from 'components/Pages/Casino/CasinoFilterContext';
import { CasinoCategoryGames } from 'components/Pages/Casino/CasinoGamesList';
import { GAME_SIZES, GROUPS } from 'components/Pages/Casino/constants';
import GameList from 'components/Pages/Casino/GameList/GameList';
import SearchResults from 'components/Pages/Casino/GameList/SearchResults';
import HomePageGames from 'components/Pages/Casino/HomePageGames';
import { ProvidersFilterBlock } from 'components/Pages/Casino/ProvidersFilterBlock';

const CATEGORY_GAMES_COUNT = 10;

// TODO: remove this when favorites is route

export const casinoSliderSettings = [
  { rows: 1, size: GAME_SIZES.MEDIUM },
  { rows: 2, size: GAME_SIZES.LARGE },
];

export const defaultCasinoSliderSettings = { rows: 1, size: GAME_SIZES.SMALL };

export const liveCasinoSliderSettings = { rows: 1, size: GAME_SIZES.MEDIUM };

export const sliderSettingsByGroup = {
  [GROUPS.CASINO]: index => casinoSliderSettings[index] || defaultCasinoSliderSettings,
  [GROUPS.LIVE]: () => liveCasinoSliderSettings,
};

const GameListSwitch = ({
  activeItem,
  homePageFunctionality,
  isSearchActive,
  scrollParent,
  searchQuery,
  setActiveItem,
  showFavorites,
}) => {
  const { filter, updateFilter } = useCasinoFilterState();
  const { group } = useCasinoNewState();
  const getSliderSettings = useCallback(index => sliderSettingsByGroup[group](index), []);
  const isHomePage = activeItem === 'home' && filter.categories.length === 0;
  const searchProviderNames = getSearchParam(window.location.search, 'providerNames');

  // function to reset showProviders variable to be true
  const resetPageToShowProviders = useCallback(() => {
    setActiveItem('all_games');
    updateFilter({ categories: [] });
  }, [setActiveItem, updateFilter]);

  useEffect(() => {
    // filter by provider on mount when there is providerNames search param
    if (homePageFunctionality && isHomePage && searchProviderNames?.split(',')?.length > 0) {
      resetPageToShowProviders();
    }
  }, [homePageFunctionality, isHomePage, resetPageToShowProviders, searchProviderNames]);

  if (isSearchActive) {
    return (
      <SearchResults
        group={group}
        homePageFunctionality={homePageFunctionality}
        scrollParent={scrollParent}
        searchQuery={searchQuery}
        sliderSettings={getSliderSettings}
      />
    );
  }

  if (showFavorites) {
    return (
      <CasinoFavoritesProvider>
        <CasinoFavoritesGames
          activeItem={activeItem}
          scrollParent={scrollParent}
          sliderSettings={getSliderSettings}
        />
      </CasinoFavoritesProvider>
    );
  }

  if (homePageFunctionality && isHomePage) {
    return (
      <>
        <CategoryGamesProvider gamesCount={CATEGORY_GAMES_COUNT} group={group}>
          <HomePageGames
            InfoGamesList={GameList}
            scrollParent={scrollParent}
            setActiveItem={setActiveItem}
          />
          <ProvidersFilterBlock onProviderClick={resetPageToShowProviders} />
        </CategoryGamesProvider>
      </>
    );
  }

  return (
    <CasinoGamesProvider
      categories={filter.categories}
      group={group}
      name=""
      providers={filter.providerNames}
    >
      <CategoryGamesProvider gamesCount={CATEGORY_GAMES_COUNT} group={group}>
        <CasinoCategoryGames
          activeItem={activeItem}
          homePageFunctionality={homePageFunctionality}
          scrollParent={scrollParent}
          setActiveItem={setActiveItem}
          sliderSettings={getSliderSettings}
        />
      </CategoryGamesProvider>
    </CasinoGamesProvider>
  );
};

export default GameListSwitch;
